import { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { buttonVariants } from "@cronika/ui";
import { links } from "@/shared/config/site";
import { useRegisterModal } from "@/core/providers/RegisterModalContext";
import { useContent, languages } from "@/shared/lib/i18n";

function useActiveSection(sectionIds: string[]) {
  const [activeId, setActiveId] = useState<string>(sectionIds[0] ?? "");

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const visible = new Set<string>();

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry?.isIntersecting) {
            visible.add(id);
          } else {
            visible.delete(id);
          }
          const ordered = sectionIds.find((s) => visible.has(s));
          if (ordered) setActiveId(ordered);
        },
        { threshold: 0.25 },
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [sectionIds.join(",")]);

  return activeId;
}

export function Header() {
  const { openModal } = useRegisterModal();
  const content = useContent();
  const [lang, setLang] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("locale") ?? "en";
    }
    return "en";
  });
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpenMobile, setLangOpenMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  function handleSetLang(code: string) {
    setLang(code);
    localStorage.setItem("locale", code);
    document.cookie = `locale=${code}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
    import("i18next").then((m) => m.default.changeLanguage(code));
  }

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 20);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const nav = content.header.nav;
  const sectionIds = nav.map((item) => item.href.replace("#", ""));
  const activeId = useActiveSection(sectionIds);

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 h-[72px] flex items-center transition-colors duration-300"
        style={scrolled ? { background: "rgba(10,10,11,0.6)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" } : undefined}
      >
        <div className="relative mx-auto w-full max-w-[1760px] px-5 lg:px-8 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5 shrink-0" aria-label={content.header.logoAriaLabel}>
            <img src="/craff-logo.svg" alt="Craff" width={29} height={42} />
            <span className="text-[18px] font-bold text-white tracking-tight">Craff</span>
          </a>

          <nav className="hidden lg:flex absolute left-1/2 -translate-x-1/2 items-center gap-1 bg-white/[0.04] border border-white/8 rounded-2xl backdrop-blur-xl">
            {nav.map((item) => {
              const isActive = item.href === `#${activeId}`;
              return isActive ? (
                <a
                  key={item.label}
                  href={item.href}
                  className="px-5 py-2 rounded-xl text-sm font-semibold text-[#070707]"
                  style={{ background: "linear-gradient(135deg, #f89a09 0%, #fec201 100%)" }}
                >
                  {item.label}
                </a>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  className="px-5 py-2 rounded-xl text-sm text-white/60 hover:text-white hover:bg-white/5 transition-all"
                >
                  {item.label}
                </a>
              );
            })}
          </nav>

          {/* Desktop right */}
          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <div ref={ref} className="relative">
              <button
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/10 bg-white/[0.04] text-sm text-white/70 hover:text-white hover:border-white/20 transition-all backdrop-blur-sm"
              >
                <GlobeIcon />
                <span className="uppercase">{lang}</span>
                <ChevronIcon open={open} />
              </button>

              {open && (
                <div
                  className="absolute right-0 top-[calc(100%+8px)] w-[200px] rounded-2xl border border-white/10 overflow-hidden py-1.5"
                  style={{
                    background: "rgba(14,14,18,0.85)",
                    backdropFilter: "blur(24px)",
                    WebkitBackdropFilter: "blur(24px)",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)",
                  }}
                >
                  {languages.map(({ code, name }) => (
                    <button
                      key={code}
                      onClick={() => {
                        handleSetLang(code);
                        setOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-all hover:bg-white/[0.06] ${
                        lang === code ? "text-[#fec201]" : "text-white/70 hover:text-white"
                      }`}
                    >
                      <span>{name}</span>
                      <span className="text-xs text-white/30 uppercase">{code}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href={links.login}
              className="inline-flex items-center justify-center h-10 px-5 rounded-xl text-sm font-medium border border-white/20 text-white hover:bg-white/5 transition-all"
            >
              {content.header.login}
            </a>

            <button onClick={openModal} className={buttonVariants({ variant: "glow" })}>
              {content.header.register}
            </button>
          </div>

          {/* Mobile right: register + burger */}
          <div className="flex lg:hidden items-center gap-3">
            <button onClick={openModal} className={buttonVariants({ variant: "glow", size: "sm" })}>
              {content.header.register}
            </button>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center justify-center w-9 h-9 text-white"
              aria-label={content.header.menuAriaLabel}
            >
              <BurgerIcon />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile burger overlay */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="fixed inset-0 z-[60] flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
            style={{
              background: "rgba(10,10,12,0.97)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
            }}
          >
            {/* Top bar */}
            <div className="flex items-center justify-between px-5 h-[72px] shrink-0">
              <a href="/" className="flex items-center gap-2.5" onClick={() => setMenuOpen(false)}>
                <img src="/craff-logo.svg" alt="Craff" width={29} height={42} />
                <span className="text-[18px] font-bold text-white tracking-tight">Craff</span>
              </a>
              <div className="flex items-center gap-3">
                <button
                  className={buttonVariants({ variant: "glow", size: "sm" })}
                  onClick={() => { openModal(); setMenuOpen(false); }}
                >
                  {content.header.register}
                </button>
                <button
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center justify-center w-9 h-9 text-white"
                  aria-label={content.header.closeAriaLabel}
                >
                  <BurgerIcon />
                </button>
              </div>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-5 pb-8">
              {/* Nav links */}
              <nav className="flex flex-col gap-2 pt-2">
                {nav.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center justify-center h-14 rounded-2xl text-base font-medium text-white"
                    style={{ background: "rgba(255,255,255,0.06)" }}
                  >
                    {item.label}
                  </a>
                ))}
              </nav>

              {/* Auth buttons */}
              <div className="flex flex-col gap-3 mt-5">
                <a
                  href={links.login}
                  className="flex items-center justify-center h-14 rounded-2xl border border-white/20 text-base font-medium text-white"
                  onClick={() => setMenuOpen(false)}
                >
                  {content.header.login}
                </a>
                <button
                  className={buttonVariants({ variant: "glow", size: "lg", className: "w-full" })}
                  onClick={() => { openModal(); setMenuOpen(false); }}
                >
                  {content.header.register}
                </button>
              </div>

              {/* Language selector */}
              <div className="mt-5">
                <button
                  onClick={() => setLangOpenMobile((v) => !v)}
                  className="w-full flex items-center justify-center gap-2 h-14 rounded-2xl text-base text-white"
                  style={{ background: "rgba(255,255,255,0.06)" }}
                >
                  <GlobeIcon />
                  <span className="uppercase">{lang}</span>
                  <ChevronIcon open={langOpenMobile} />
                </button>

                {langOpenMobile && (
                  <div className="mt-2 flex flex-col">
                    {languages.map(({ code, name }) => (
                      <button
                        key={code}
                        onClick={() => {
                          handleSetLang(code);
                          setLangOpenMobile(false);
                        }}
                        className={`py-3 text-center text-base transition-colors ${
                          lang === code ? "text-[#fec201]" : "text-white/60 hover:text-white"
                        }`}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function BurgerIcon() {
  return (
    <svg width="22" height="16" viewBox="0 0 22 16" fill="none">
      <path d="M6 1h15M1 8h20M12 15h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <circle cx="7.5" cy="7.5" r="6.5" stroke="currentColor" strokeWidth="1.2" />
      <ellipse cx="7.5" cy="7.5" rx="3" ry="6.5" stroke="currentColor" strokeWidth="1.2" />
      <line x1="1" y1="7.5" x2="14" y2="7.5" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      className="transition-transform duration-200"
      style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
    >
      <path
        d="M2.5 3.75L5 6.25L7.5 3.75"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
